import { useEffect, useRef, useState, type CSSProperties, type ReactNode, type DragEvent, type TouchEvent } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { aparecer, desplegar, pop, resorte, subir, suave } from '../components/movimiento';
import { api, textoUbicacion, type NivelPosicion, type PalletSinUbicar, type Rack } from '../api';
import { mensajeDeError, useApi, VIVO } from '../lib/useApi';
import { useEsCelular } from '../lib/useMedia';
import { useEstadoGuardado } from '../lib/persistencia';
import { Buscador, Cargando, EstadoChip, filtrar, Icono, Numero, PanelError, Vacio } from '../components/ui';
import { aviso, botonPrimario, botonSecundario, c, campo, mono, subtitulo, tarjeta } from '../theme';

/**
 * Ubicar pallets arrastrándolos: los racks ocupan la pantalla y a la derecha
 * espera la cola de lo que todavía no tiene lugar.
 *
 * Es la misma operación que Ubicaciones (`/mapa/posiciones/:codigo/asignar`),
 * pero para cargar un contenedor entero: en el plano hay que abrir cada hueco
 * y elegir el pallet de una lista, veinte veces. Acá se suelta y listo.
 *
 * Con mouse el arrastre es el nativo del navegador. Con el dedo ése no anda,
 * así que hay uno propio (`useArrastreTactil`): se mantiene apretado el pallet
 * un instante y se lo lleva. Y también se puede tocar el pallet y después el
 * hueco: mismo gesto en dos toques.
 */

/** Adónde va: un hueco de un rack, una zona de piso (`nivel` null), o `null` para devolverlo a la cola. */
type Destino = { codigo: string; nivel: number | null } | null;

const TIPO = 'text/x-pallet';

/** `A1-04#2` es el nivel 2 de la posición A1-04; `P1-01#zona`, la zona de piso P1; `cola` es la cola. */
const destinoDe = (clave: string): Destino => {
  if (clave === 'cola') return null;
  const [codigo, nivel] = clave.split('#');
  return { codigo: codigo!, nivel: nivel === 'zona' ? null : Number(nivel) };
};

/** Dónde se puede soltar lo que está debajo del dedo, si es que algo. */
const claveEn = (x: number, y: number) =>
  document.elementFromPoint(x, y)?.closest('[data-destino]')?.getAttribute('data-destino') ?? null;

/** Cuánto hay que mantener apretado para agarrar: menos, y cada scroll agarra un pallet. */
const ESPERA_MS = 400;

/**
 * El arrastre con el dedo.
 *
 * Mantener apretado es lo que lo separa del scroll: si el dedo se mueve antes
 * de `ESPERA_MS`, es alguien bajando por los racks y no se hace nada. Pasado
 * ese tiempo el pallet se agarra, la página deja de desplazarse con el dedo
 * (por eso el `touchmove` va registrado a mano, sin `passive`: el de React no
 * puede frenar el scroll) y un fantasma lo sigue. Cerca de los bordes la
 * página baja o sube sola, que si no un rack de abajo no se alcanza nunca.
 */
function useArrastreTactil(onSoltar: (pallet: string, clave: string) => void, onSobre: (clave: string | null) => void) {
  const [fantasma, setFantasma] = useState<{ pallet: string; x: number; y: number } | null>(null);
  /** Se está manteniendo apretado y todavía no se agarró: es lo que muestra el anillo que carga. */
  const [apretando, setApretando] = useState<{ x: number; y: number } | null>(null);
  const actual = useRef<{ pallet: string; x: number; y: number; activo: boolean; espera: number } | null>(null);
  /** El toque que termina un arrastre también dispara un click: ése no elige nada. */
  const recienSoltado = useRef(false);
  const callbacks = useRef({ onSoltar, onSobre });
  callbacks.current = { onSoltar, onSobre };

  useEffect(() => {
    let cuadro = 0;
    const desplazar = () => {
      const d = actual.current;
      if (!d?.activo) return;
      const tope = document.getElementById('wms-bandeja')?.getBoundingClientRect().top ?? window.innerHeight;
      if (d.y < 90) window.scrollBy(0, -14);
      else if (d.y > tope - 70 && d.y < tope) window.scrollBy(0, 14);
      cuadro = requestAnimationFrame(desplazar);
    };

    const mover = (e: globalThis.TouchEvent) => {
      const d = actual.current;
      const t = e.touches[0];
      if (!d || !t) return;
      if (!d.activo) {
        if (Math.hypot(t.clientX - d.x, t.clientY - d.y) > 10) {
          clearTimeout(d.espera);
          actual.current = null;
          setApretando(null);
        }
        return;
      }
      e.preventDefault();
      d.x = t.clientX;
      d.y = t.clientY;
      setFantasma({ pallet: d.pallet, x: d.x, y: d.y });
      callbacks.current.onSobre(claveEn(d.x, d.y));
    };

    const terminar = (e: globalThis.TouchEvent) => {
      const d = actual.current;
      actual.current = null;
      setApretando(null);
      if (!d) return;
      clearTimeout(d.espera);
      if (!d.activo) return;
      cancelAnimationFrame(cuadro);
      recienSoltado.current = true;
      setTimeout(() => (recienSoltado.current = false), 400);
      setFantasma(null);
      callbacks.current.onSobre(null);
      const clave = e.type === 'touchend' ? claveEn(d.x, d.y) : null;
      if (clave) callbacks.current.onSoltar(d.pallet, clave);
    };

    const empezar = () => {
      cancelAnimationFrame(cuadro);
      cuadro = requestAnimationFrame(desplazar);
    };
    document.addEventListener('wms-arrastre', empezar);
    document.addEventListener('touchmove', mover, { passive: false });
    document.addEventListener('touchend', terminar);
    document.addEventListener('touchcancel', terminar);
    return () => {
      cancelAnimationFrame(cuadro);
      document.removeEventListener('wms-arrastre', empezar);
      document.removeEventListener('touchmove', mover);
      document.removeEventListener('touchend', terminar);
      document.removeEventListener('touchcancel', terminar);
    };
  }, []);

  /** Lo que se le pone a cada pallet que se puede agarrar con el dedo. */
  const tocable = (pallet: string) => ({
    onTouchStart: (e: TouchEvent) => {
      const t = e.touches[0];
      if (!t || e.touches.length > 1) return;
      const d = { pallet, x: t.clientX, y: t.clientY, activo: false, espera: 0 };
      d.espera = window.setTimeout(() => {
        if (actual.current !== d) return;
        d.activo = true;
        setApretando(null);
        setFantasma({ pallet, x: d.x, y: d.y });
        navigator.vibrate?.(15);
        document.dispatchEvent(new Event('wms-arrastre'));
      }, ESPERA_MS);
      actual.current = d;
      setApretando({ x: t.clientX, y: t.clientY });
    },
    // Mantener apretado abre el menú del navegador: acá mantener es agarrar.
    onContextMenu: (e: { preventDefault: () => void }) => e.preventDefault(),
  });

  return { fantasma, apretando, tocable, recienSoltado };
}

/** `A1-04 N2`, «P1 · zona de piso» o «a la cola»: cómo se nombra un destino en los avisos. */
const nombreDe = (d: Destino) => (!d ? 'a la cola' : d.nivel === null ? textoUbicacion(d.codigo, null) : `${d.codigo} N${d.nivel}`);

/**
 * El pallet que se acaba de soltar viaja de donde estaba a su hueco en vez de
 * aparecer ahí: el lugar viejo y el nuevo llevan la misma `layoutId`.
 *
 * Es un envoltorio y no el pallet mismo a propósito: Motion se queda con los
 * `onDragStart`/`onDragEnd` de un `motion.*` (son los de su propio arrastre) y
 * el arrastre nativo dejaría de andar. Además sólo envuelve al que viaja, y
 * recién después de soltarlo: lo que se está arrastrando nunca se anima.
 */
function Viaje({ pallet, viajero, style, children }: { pallet: string; viajero: string | null; style?: CSSProperties; children: ReactNode }) {
  if (pallet !== viajero) return <>{children}</>;
  return (
    <motion.div layoutId={`ubicar-${pallet}`} transition={{ layout: resorte }} style={{ position: 'relative', zIndex: 2, ...style }}>
      {children}
    </motion.div>
  );
}

/** Cuánto dura el viaje del pallet soltado: lo que tarda el mapa en volver con el pallet en su lugar nuevo. */
const VIAJE_MS = 2_000;

/** Cuánto queda a mano el «Deshacer»: lo justo para darse cuenta de que se soltó mal. */
const DESHACER_MS = 8_000;

export default function Ubicar({
  pallet,
}: {
  /** El que tiene que quedar elegido al entrar: viene de escanear o de un link (`#/ubicar/PLT-…`). */
  pallet?: string;
}) {
  const celular = useEsCelular();
  const { datos, cargando, error, recargar } = useApi(() => api.mapa(''), [], VIVO.normal);
  const [elegido, setElegido] = useState<string | null>(null);
  const [moviendo, setMoviendo] = useState<string | null>(null);
  /** El que se acaba de soltar: viaja animado hasta su hueco cuando el mapa vuelve (`Viaje`). */
  const [viajero, setViajero] = useState<string | null>(null);
  const [sobre, setSobre] = useState<string | null>(null);
  const [falla, setFalla] = useState('');
  /** El último movimiento, para poder volverlo atrás: con el dedo se suelta mal seguido. */
  const [hecho, setHecho] = useState<{ pallet: string; desde: Destino; hasta: Destino } | null>(null);
  /** Un pallet que hay que mostrar en pantalla: se desplaza hasta él después de dibujar. */
  const [enfocar, setEnfocar] = useState<string | null>(null);
  const [busqueda, setBusqueda] = useState('');
  /** '' es todos; si no, el id del contenedor. */
  const [contenedor, setContenedor] = useState('');
  /** Los racks plegados, por id: así se llega al que interesa sin bajar por todos. */
  const [cerrados, setCerrados] = useEstadoGuardado<string[]>('ubicar.cerrados', [], Array.isArray);
  const alternar = (ids: string[], cerrar: boolean) =>
    setCerrados((xs) => (cerrar ? [...new Set([...xs, ...ids])] : xs.filter((x) => !ids.includes(x))));

  /** Cada pallet que está en un rack, con dónde: de acá salen la búsqueda, el deshacer y el pallet del link. */
  const enRacks = (datos?.sectores ?? []).flatMap((s) =>
    s.racks.flatMap((r) =>
      r.posiciones.flatMap((p) =>
        [
          ...p.niveles.filter((n) => n.pallet).map((n) => ({ ...n, nivel: n.nivel as number | null })),
          ...(p.zona ?? []).map((n) => ({ ...n, nivel: null })),
        ].map((n) => ({ pallet: n.pallet!, articulo: n.articulo, rack: r.id, codigo: p.codigo, nivel: n.nivel })),
      ),
    ),
  );
  /** Dónde está hoy: `null` es la cola, `undefined` que no está en ningún lado. */
  const ubicacion = (id: string): Destino | undefined => {
    if (datos?.sinUbicar.some((p) => p.id === id)) return null;
    const h = enRacks.find((x) => x.pallet === id);
    return h ? { codigo: h.codigo, nivel: h.nivel } : undefined;
  };

  // El pallet que llega por la ruta se atiende una sola vez: el mapa se
  // recarga solo cada tanto y no puede volver a elegirlo después de soltarlo.
  const atendido = useRef<string | null>(null);
  useEffect(() => {
    if (!pallet || !datos || atendido.current === pallet) return;
    atendido.current = pallet;
    const donde = ubicacion(pallet);
    if (donde === undefined) {
      setFalla(`No encontramos ${pallet}.`);
      return;
    }
    setElegido(pallet);
    setEnfocar(pallet);
    if (donde === null) {
      // Que ningún filtro lo esconda de la cola.
      setContenedor('');
      setBusqueda('');
    } else {
      const rack = enRacks.find((x) => x.pallet === pallet)?.rack;
      if (rack) alternar([rack], false);
    }
  }, [pallet, datos]); // eslint-disable-line react-hooks/exhaustive-deps

  // Va en un efecto aparte porque el rack recién abierto recién existe en el
  // render siguiente: antes no hay a qué desplazarse.
  useEffect(() => {
    if (!enfocar) return;
    document
      .querySelector(`[data-pallet="${CSS.escape(enfocar)}"]`)
      ?.scrollIntoView({ block: 'center', inline: 'center', behavior: 'smooth' });
    setEnfocar(null);
  }, [enfocar]);

  useEffect(() => {
    if (!hecho) return;
    const id = setTimeout(() => setHecho(null), DESHACER_MS);
    return () => clearTimeout(id);
  }, [hecho]);
  const tactil = useArrastreTactil(
    (pallet, clave) => {
      // Soltarlo en la cola cuando ya estaba en la cola no es mover nada.
      if (clave === 'cola' && datos?.sinUbicar.some((p) => p.id === pallet)) return;
      void mover(pallet, destinoDe(clave));
    },
    setSobre,
  );

  /** `deshaciendo` es volverlo atrás: eso no deja a su vez otro «Deshacer». */
  async function mover(pallet: string, destino: Destino, deshaciendo = false) {
    // Se mira antes de mover: después del pedido el mapa ya no sabe de dónde venía.
    const desde = ubicacion(pallet);
    setElegido(null);
    setSobre(null);
    setMoviendo(pallet);
    setViajero(pallet);
    try {
      if (destino) await api.asignarPosicion(destino.codigo, pallet, destino.nivel);
      else await api.desubicar(pallet);
      setFalla('');
      setHecho(deshaciendo || desde === undefined ? null : { pallet, desde, hasta: destino });
    } catch (err) {
      setFalla(mensajeDeError(err));
    } finally {
      setMoviendo(null);
      recargar();
      // ponytail: plazo fijo; si el mapa tarda más que esto en volver, el pallet aparece sin viajar.
      setTimeout(() => setViajero((v) => (v === pallet ? null : v)), VIAJE_MS);
    }
  }

  /** Lo que tiene que hacer cualquier lugar donde se puede soltar un pallet. */
  const soltable = (clave: string, destino: Destino) => ({
    'data-destino': clave,
    onDragOver: (e: DragEvent) => {
      if (!e.dataTransfer.types.includes(TIPO)) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      if (sobre !== clave) setSobre(clave);
    },
    onDragLeave: () => setSobre((s) => (s === clave ? null : s)),
    onDrop: (e: DragEvent) => {
      e.preventDefault();
      const pallet = e.dataTransfer.getData(TIPO);
      if (pallet) void mover(pallet, destino);
    },
  });

  /** Lo que tiene que hacer cualquier pallet que se puede agarrar. */
  const agarrable = (pallet: string) => ({
    // Por acá lo encuentra `enfocar` para desplazarse hasta él.
    'data-pallet': pallet,
    // Con el dedo arrastra `useArrastreTactil`: el nativo, en el celular, pelea con el scroll.
    draggable: moviendo === null && !celular,
    ...(moviendo === null ? tactil.tocable(pallet) : {}),
    onDragStart: (e: DragEvent) => {
      e.dataTransfer.setData(TIPO, pallet);
      e.dataTransfer.effectAllowed = 'move';
      setElegido(null);
    },
    onDragEnd: () => setSobre(null),
    onClick: (e: { stopPropagation: () => void }) => {
      e.stopPropagation();
      if (tactil.recienSoltado.current) return;
      setElegido((x) => (x === pallet ? null : pallet));
    },
  });

  const viaja = tactil.fantasma ? null : viajero;

  if (error) return <PanelError mensaje={error} onReintentar={recargar} />;
  if (cargando && !datos) return <Cargando texto="Trayendo los racks…" />;
  if (!datos) return null;

  // Los contenedores que tienen algo en la cola, del más viejo al más nuevo.
  const contenedores = [
    ...new Map(datos.sinUbicar.filter((p) => p.contenedor).map((p) => [p.contenedor!, p.contenedorNumero])),
  ].sort((a, b) => (a[1] ?? Infinity) - (b[1] ?? Infinity) || a[0].localeCompare(b[0]));
  // Si el contenedor elegido ya se ubicó entero, deja de filtrar.
  const filtro = contenedores.some(([id]) => id === contenedor) ? contenedor : '';
  const cola = filtrar(
    datos.sinUbicar.filter((p) => !filtro || p.contenedor === filtro),
    busqueda,
    (p) => [p.id, p.articulo],
  );
  // El buscador también encuentra lo que ya está en los racks: se resalta ahí
  // mismo, que es donde hay que ir a buscarlo.
  const coinciden = (texto: string) =>
    texto.trim() ? filtrar(enRacks, texto, (h) => [h.pallet, h.articulo]) : [];
  const resaltados = new Set(coinciden(busqueda).map((h) => h.pallet));
  const buscar = (texto: string) => {
    setBusqueda(texto);
    // Un rack plegado con una coincidencia adentro la escondería: se abre.
    const ids = [...new Set(coinciden(texto).map((h) => h.rack))];
    if (ids.length) alternar(ids, false);
  };

  const filtros = datos.sinUbicar.length > 0 && (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
      {contenedores.length > 1 && (
        <select
          value={filtro}
          onChange={(e) => setContenedor(e.target.value)}
          aria-label="Contenedor"
          style={{ ...campo, height: 40, flex: '1 1 160px', width: 'auto' }}
        >
          <option value="">Todos los contenedores</option>
          {contenedores.map(([id, numero]) => (
            <option key={id} value={id}>
              {numero ? `Nº ${numero} · ${id}` : id}
            </option>
          ))}
        </select>
      )}
      <Buscador valor={busqueda} onCambiar={buscar}placeholder="Pallet o código de producto" ancho={celular ? 9999 : 276} />
    </div>
  );
  // De la A a la Z, que es como se los busca parado en el pasillo. `numeric`
  // pone el «Sector 2» antes que el «Sector 10».
  const sectores = datos.sectores
    .filter((s) => s.racks.length > 0)
    .sort((a, b) => a.nombre.localeCompare(b.nombre, 'es', { numeric: true, sensitivity: 'base' }));

  // El primer hueco libre para el elegido, en el orden en que se recorre el
  // depósito: sector, rack, columna y de abajo hacia arriba (abajo se carga
  // sin autoelevador alto). Ahorra buscar un hueco con la vista en un depósito lleno.
  const primerLibre: Destino = elegido
    ? (sectores
        // La zona de piso no se sugiere: es para los pallets que no entran en un rack.
        .flatMap((s) => s.racks.filter((r) => r.tipo !== 'piso').sort((a, b) => a.numero - b.numero))
        .flatMap((r) =>
          [...r.posiciones]
            .sort((a, b) => a.columna - b.columna)
            .flatMap((p) =>
              Array.from({ length: r.niveles }, (_, i) => i + 1)
                .filter((n) => !p.niveles.find((x) => x.nivel === n)?.pallet)
                .map((n) => ({ codigo: p.codigo, nivel: n })),
            ),
        )[0] ?? null)
    : null;
  const alPrimerLibre = () => moviendo === null && elegido && primerLibre && void mover(elegido, primerLibre);

  // El error y el «Deshacer» van pegados a la cola (en el celular, a la
  // bandeja): arriba de todo no se ven con los racks recorridos.
  // Entran animados para que el «Deshacer» se note recién aparece: en el
  // celular suben desde la bandeja, en PC caen arriba de la cola. La clave es
  // fija: con una por movimiento, el viejo saliendo y el nuevo entrando
  // quedaban un instante apilados y la bandeja daba un salto.
  const entrada = celular ? subir : aparecer;
  const avisos = (
    <AnimatePresence initial={false}>
      {hecho && (
        <motion.div key="hecho" {...entrada}>
          <Franja tono="ok" enBandeja={celular} boton="Deshacer" onBoton={() => void mover(hecho.pallet, hecho.desde, true)}>
            <strong style={{ fontFamily: mono }}>{hecho.pallet}</strong> → {nombreDe(hecho.hasta)}
          </Franja>
        </motion.div>
      )}
      {falla && (
        <motion.div key="falla" {...entrada}>
          <Franja tono="err" enBandeja={celular} boton={<Icono nombre="close" size={19} />} etiqueta="Cerrar el aviso" onBoton={() => setFalla('')}>
            {falla}
          </Franja>
        </motion.div>
      )}
    </AnimatePresence>
  );

  const colaVista = (
    <div
      {...soltable('cola', null)}
      onClick={() => moviendo === null && elegido && void mover(elegido, null)}
      style={{
        ...tarjeta,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        minHeight: 0,
        outline: sobre === 'cola' ? `2px dashed ${c.verde}` : undefined,
        outlineOffset: -2,
      }}
    >
      <div style={{ padding: '14px 16px', borderBottom: `1px solid ${c.borde}`, display: 'flex', gap: 10, alignItems: 'center' }}>
        <Icono nombre="pallet" size={19} color={c.ambar} />
        <span style={{ flex: 1, fontSize: 15, fontWeight: 600, color: c.texto }}>Sin ubicar</span>
        <EstadoChip
          estado={cola.length === datos.sinUbicar.length ? String(cola.length) : `${cola.length} de ${datos.sinUbicar.length}`}
          tono={datos.sinUbicar.length > 0 ? 'warn' : 'ok'}
        />
      </div>
      {filtros && <div style={{ padding: '10px 12px 0' }}>{filtros}</div>}
      <div style={{ overflow: 'auto', padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
        {cola.length === 0 ? (
          <div style={{ padding: '24px 8px', textAlign: 'center', fontSize: 13, color: c.texto3 }}>
            {datos.sinUbicar.length === 0 ? 'Todo ubicado. Soltá acá un pallet para sacarlo de su lugar.' : 'Nada con ese filtro.'}
          </div>
        ) : (
          cola.map((p) => (
            <Viaje key={p.id} pallet={p.id} viajero={viaja}>
            <div
              {...agarrable(p.id)}
              title="Arrastralo a un hueco libre, o tocalo y después tocá el hueco"
              style={{
                ...pastilla(elegido === p.id, moviendo === p.id),
                padding: '9px 12px',
              }}
            >
              <span style={{ fontFamily: mono, fontSize: 13, fontWeight: 600, color: c.texto }}>{p.id}</span>
              <span style={{ fontSize: 12, color: c.texto2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {p.articulo} · {p.tipo === 'completo' ? 'entero' : `${p.piezas} pz`}
              </span>
            </div>
            </Viaje>
          ))
        )}
      </div>
    </div>
  );

  return (
    <div
      style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingBottom: celular ? ALTO_BANDEJA + 16 : 0 }}
      onClick={() => setElegido(null)}
    >
      <div>
        <div style={{ ...subtitulo, marginTop: 0 }}>
          {celular
            ? 'Mantené apretado un pallet de la bandeja y arrastralo al hueco, o tocalo y después tocá el hueco. Los que ya están en un rack se mueven igual.'
            : 'Arrastrá cada pallet de la derecha a un hueco libre. También se mueve de un hueco a otro, o se devuelve a la cola.'}
        </div>
      </div>

      <AnimatePresence initial={false}>
      {elegido && !celular && (
        <motion.div {...desplegar}>
        <div style={{ ...aviso('ok'), display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }} onClick={(e) => e.stopPropagation()}>
          <Icono nombre="touch_app" size={19} color={c.verde} />
          <span style={{ flex: 1 }}>
            <strong style={{ fontFamily: mono }}>{elegido}</strong>: tocá el hueco libre donde va, o la cola para sacarlo.
          </span>
          {primerLibre && (
            <button type="button" onClick={alPrimerLibre} style={{ ...botonPrimario, minHeight: 44, padding: '6px 14px' }}>
              Al primer libre ({nombreDe(primerLibre)})
            </button>
          )}
          <button type="button" onClick={() => setElegido(null)} style={{ ...botonSecundario, minHeight: 44, padding: '6px 14px' }}>
            Cancelar
          </button>
        </div>
        </motion.div>
      )}
      </AnimatePresence>

      {/* En tablet la cola se angosta: a 300 px se comía media pantalla y
          los racks quedaban cortados en la tercera columna. El corte de una
          columna coincide con `useEsCelular`, que es el que pone la bandeja. */}
      <div
        className="grid grid-cols-1 tablet:grid-cols-[minmax(0,1fr)_250px] escritorio:grid-cols-[minmax(0,1fr)_300px]"
        style={{ gap: 16, alignItems: 'start' }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, minWidth: 0 }}>
          {sectores.length === 0 ? (
            <div style={tarjeta}>
              <Vacio icono="grid_view" titulo="Todavía no hay racks" detalle="Se cargan en Planta del depósito." />
            </div>
          ) : (
            sectores.map((s) => {
              const ids = s.racks.map((r) => r.id);
              const todosCerrados = ids.every((id) => cerrados.includes(id));
              return (
                // En el celular 4 px menos por lado: es lo que falta para que entre
                // la tercera columna del rack entera en vez de cortada.
                <div key={s.id} className="p-3 tablet:p-4" style={{ ...tarjeta, display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {/* En el celular el conteo va debajo del nombre: al lado, con el
                      botón, quedaba en una columna angosta y se partía en dos. */}
                  <div className="items-center tablet:items-baseline" style={{ display: 'flex', gap: 10 }}>
                    <div className="flex flex-1 flex-col tablet:flex-row tablet:items-baseline tablet:gap-2.5" style={{ minWidth: 0 }}>
                      <span style={{ fontSize: 15, fontWeight: 600, color: c.texto }}>{s.nombre}</span>
                      <span style={{ fontSize: 12.5, color: c.texto3 }}>
                        <Numero valor={s.llenos} /> de <Numero valor={s.slots} /> ocupados
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        alternar(ids, !todosCerrados);
                      }}
                      style={{ ...botonSecundario, minHeight: 44, padding: '5px 14px', fontSize: 12.5 }}
                    >
                      <AnimatePresence mode="wait" initial={false}>
                        <motion.span key={String(todosCerrados)} {...aparecer} style={{ display: 'inline-block' }}>
                          {todosCerrados ? 'Abrir todos' : 'Cerrar todos'}
                        </motion.span>
                      </AnimatePresence>
                    </button>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14 }}>
                    {s.racks.map((r) => (
                      <RackGrilla
                        key={r.id}
                        rack={r}
                        cerrado={cerrados.includes(r.id)}
                        onAlternar={() => alternar([r.id], !cerrados.includes(r.id))}
                        sobre={sobre}
                        elegido={elegido}
                        moviendo={moviendo}
                        viajero={viaja}
                        resaltados={resaltados}
                        sugerido={primerLibre && `${primerLibre.codigo}#${primerLibre.nivel}`}
                        soltable={soltable}
                        agarrable={agarrable}
                        onHueco={(destino) => moviendo === null && elegido && void mover(elegido, destino)}
                      />
                    ))}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {!celular && (
          <div style={{ position: 'sticky', top: 16, maxHeight: 'calc(100vh - 32px)', display: 'flex', flexDirection: 'column', gap: 10 }}>
            {avisos}
            {colaVista}
          </div>
        )}
      </div>

      {/* Mientras se mantiene apretado, un anillo se llena alrededor del dedo:
          cuando cierra, el pallet está agarrado. Sin esto no se sabe si hay
          que seguir apretando o si no pasó nada. */}
      {tactil.apretando && (
        <svg
          width={64}
          height={64}
          viewBox="0 0 36 36"
          style={{
            position: 'fixed',
            left: tactil.apretando.x - 32,
            top: tactil.apretando.y - 32,
            zIndex: 40,
            pointerEvents: 'none',
          }}
          aria-hidden
        >
          <circle cx={18} cy={18} r={15.9} fill="rgba(0,122,71,.10)" stroke={c.verdeBorde} strokeWidth={3} />
          <circle
            cx={18}
            cy={18}
            r={15.9}
            fill="none"
            stroke={c.verde}
            strokeWidth={3}
            strokeLinecap="round"
            pathLength={100}
            strokeDasharray={100}
            transform="rotate(-90 18 18)"
            style={{ animation: `wms-carga ${ESPERA_MS}ms linear forwards` }}
          />
        </svg>
      )}

      {tactil.fantasma && (
        <div
          style={{
            ...pastilla(true, false),
            position: 'fixed',
            left: tactil.fantasma.x - 60,
            top: tactil.fantasma.y - 56,
            zIndex: 40,
            padding: '8px 12px',
            pointerEvents: 'none',
            boxShadow: '0 8px 24px rgba(0,0,0,.25)',
          }}
        >
          <span style={{ fontFamily: mono, fontSize: 13, fontWeight: 600, color: c.texto }}>{tactil.fantasma.pallet}</span>
        </div>
      )}

      {celular && (
        <Bandeja
          sobre={sobre === 'cola'}
          agarrable={agarrable}
          cola={cola}
          total={datos.sinUbicar.length}
          filtros={filtros}
          elegido={elegido}
          moviendo={moviendo}
          viajero={viaja}
          avisos={avisos}
          primerLibre={primerLibre && nombreDe(primerLibre)}
          onPrimerLibre={alPrimerLibre}
          enRack={elegido !== null && !datos.sinUbicar.some((p) => p.id === elegido)}
          onCancelar={() => setElegido(null)}
          onALaCola={() => moviendo === null && elegido && void mover(elegido, null)}
        />
      )}
    </div>
  );
}

/** Lo que ocupa la bandeja de abajo en el celular; la página deja ese hueco al final. */
const ALTO_BANDEJA = 200;

/**
 * En el celular la cola vive pegada abajo de la pantalla: los racks se
 * recorren con el dedo, y una cola arriba de todo quedaba lejos apenas se
 * bajaba al segundo sector. Así está siempre a un toque: se elige el pallet
 * acá y se toca el hueco donde va.
 */
function Bandeja({
  sobre,
  agarrable,
  cola,
  total,
  filtros,
  elegido,
  moviendo,
  viajero,
  avisos,
  primerLibre,
  onPrimerLibre,
  enRack,
  onCancelar,
  onALaCola,
}: {
  /** Hay un pallet arrastrado encima: se suelta para devolverlo a la cola. */
  sobre: boolean;
  agarrable: (pallet: string) => object;
  /** Ya filtrada por contenedor y búsqueda. */
  cola: PalletSinUbicar[];
  /** Todos los sin ubicar, sin filtro. */
  total: number;
  filtros: ReactNode;
  elegido: string | null;
  moviendo: string | null;
  viajero: string | null;
  /** El error y el «Deshacer». Van acá y no arriba: con los racks recorridos, arriba no se ve. */
  avisos: ReactNode;
  /** El primer hueco libre para el elegido, ya nombrado (`A1-04 N1`), o null si no queda ninguno. */
  primerLibre: string | null;
  onPrimerLibre: () => void;
  /** El elegido está en un rack: se ofrece devolverlo a la cola. */
  enRack: boolean;
  onCancelar: () => void;
  onALaCola: () => void;
}) {
  const [abierta, setAbierta] = useState(true);
  // Sube al entrar, como cualquier bandeja del celular. La transformación es
  // de la bandeja misma, que es la fija: no tiene hijos fijos que se rompan.
  return (
    <motion.div
      {...subir}
      id="wms-bandeja"
      data-destino="cola"
      onClick={(e) => e.stopPropagation()}
      style={{
        outline: sobre ? `2px dashed ${c.verde}` : undefined,
        outlineOffset: -4,
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 30,
        background: c.panel,
        borderTop: `1px solid ${c.borde}`,
        boxShadow: '0 -6px 20px rgba(0,0,0,.10)',
        paddingBottom: 'env(safe-area-inset-bottom)',
      }}
    >
      {avisos}
      {/* Con un elegido son hasta tres botones de 44 px y no entran en una
          línea con el texto: arriba el pallet con «Cancelar» (que es soltarlo),
          abajo las acciones repartidas a lo ancho. Antes se acomodaban como
          caían y «Cancelar» terminaba solo en un renglón. */}
      {/* Elegir un pallet cambia la cabecera por sus acciones: una se va y la otra entra, sin salto. */}
      <div style={{ padding: '10px 16px', minHeight: 48, display: 'flex', alignItems: 'center' }}>
      <AnimatePresence mode="wait" initial={false}>
        {elegido ? (
          <motion.div
            key="elegido"
            {...aparecer}
            style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 8 }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Icono nombre="touch_app" size={19} color={c.verde} />
              <span style={{ flex: 1, minWidth: 0, fontSize: 13, color: c.texto }}>
                <strong style={{ fontFamily: mono }}>{elegido}</strong>: tocá el hueco donde va
              </span>
              <button type="button" onClick={onCancelar} style={{ ...botonSecundario, minHeight: 44, padding: '6px 12px', fontSize: 12.5, flexShrink: 0 }}>
                Cancelar
              </button>
            </div>
            {(primerLibre || enRack) && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {primerLibre && (
                  <button type="button" onClick={onPrimerLibre} style={{ ...botonPrimario, minHeight: 44, padding: '6px 12px', fontSize: 12.5, flex: '1 1 auto', justifyContent: 'center' }}>
                    Al primer libre ({primerLibre})
                  </button>
                )}
                {enRack && (
                  <button type="button" onClick={onALaCola} style={{ ...botonSecundario, minHeight: 44, padding: '6px 12px', fontSize: 12.5, flex: '1 1 auto', justifyContent: 'center' }}>
                    Sacar del rack
                  </button>
                )}
              </div>
            )}
          </motion.div>
        ) : (
          <motion.button
            key="cabecera"
            {...aparecer}
            type="button"
            onClick={() => setAbierta((v) => !v)}
            aria-expanded={abierta}
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              border: 'none',
              background: 'none',
              padding: 0,
              font: 'inherit',
              textAlign: 'left',
            }}
          >
            <Icono nombre="pallet" size={19} color={c.ambar} />
            <span style={{ flex: 1, fontSize: 14, fontWeight: 600, color: c.texto }}>Sin ubicar</span>
            <EstadoChip
              estado={cola.length === total ? String(total) : `${cola.length} de ${total}`}
              tono={total > 0 ? 'warn' : 'ok'}
            />
            {/* Una sola flecha que gira: abierta apunta abajo, plegada arriba. */}
            <motion.span animate={{ rotate: abierta ? 0 : 180 }} transition={suave} style={{ display: 'grid' }}>
              <Icono nombre="expand_more" size={20} color={c.texto3} />
            </motion.span>
          </motion.button>
        )}
      </AnimatePresence>
      </div>

      {/* Se pliega y despliega en el lugar: lo que se anima es la altura del
          contenedor, nunca las pastillas que se arrastran. */}
      <AnimatePresence initial={false}>
      {abierta && !elegido && filtros && (
        <motion.div key="filtros" {...desplegar}>
          <div style={{ padding: '0 16px 10px' }}>{filtros}</div>
        </motion.div>
      )}
      {(abierta || elegido) && (
        <motion.div key="cola" {...desplegar}>
        <div
          style={{
            display: 'flex',
            gap: 8,
            overflowX: 'auto',
            padding: '0 16px 12px',
            scrollSnapType: 'x proximity',
          }}
        >
          {cola.length === 0 ? (
            <span style={{ fontSize: 13, color: c.texto3, padding: '8px 0' }}>
              {total === 0 ? 'Todo ubicado.' : 'Nada con ese filtro.'}
            </span>
          ) : (
            cola.map((p) => (
              <Viaje key={p.id} pallet={p.id} viajero={viajero} style={{ flex: '0 0 150px', display: 'flex', flexDirection: 'column' }}>
              <button
                type="button"
                {...agarrable(p.id)}
                style={{
                  ...pastilla(elegido === p.id, moviendo === p.id),
                  flex: '0 0 150px',
                  padding: '9px 12px',
                  textAlign: 'left',
                  font: 'inherit',
                  cursor: 'pointer',
                  scrollSnapAlign: 'start',
                }}
              >
                <span style={{ fontFamily: mono, fontSize: 13, fontWeight: 600, color: c.texto }}>{p.id}</span>
                <span
                  style={{ fontSize: 12, color: c.texto2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                >
                  {p.articulo} · {p.tipo === 'completo' ? 'entero' : `${p.piezas} pz`}
                </span>
              </button>
              </Viaje>
            ))
          )}
        </div>
        </motion.div>
      )}
      </AnimatePresence>
    </motion.div>
  );
}

/** El aspecto de un pallet que se puede agarrar, elegido o en viaje. */
const pastilla = (elegido: boolean, moviendo: boolean): CSSProperties => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  minWidth: 0,
  borderRadius: 10,
  border: `1px solid ${elegido ? c.verde : c.bordeFuerte}`,
  background: elegido ? c.verdeSuave : c.panel,
  boxShadow: elegido ? `0 0 0 2px ${c.verdeBorde}` : '0 1px 2px rgba(0,0,0,.06)',
  cursor: 'grab',
  opacity: moviendo ? 0.45 : 1,
  // Elegir y soltar cambian el color de a poco. Sólo colores y opacidad: una
  // transformación acá movería el punto de agarre.
  transition: 'background-color .34s cubic-bezier(.16,1,.3,1), border-color .34s cubic-bezier(.16,1,.3,1), box-shadow .34s cubic-bezier(.16,1,.3,1), opacity .34s cubic-bezier(.16,1,.3,1)',
  userSelect: 'none',
  WebkitUserSelect: 'none',
  WebkitTouchCallout: 'none',
});

/**
 * Un aviso de una línea con su botón al costado: el «Deshacer» y el error
 * tienen la misma forma, al lado de la cola en PC y adentro de la bandeja en el
 * celular. El botón mide 44 px: se toca con guantes.
 */
function Franja({
  tono,
  enBandeja,
  boton,
  etiqueta,
  onBoton,
  children,
}: {
  tono: 'ok' | 'err';
  /** Pegada al borde de la bandeja: sin esquinas redondeadas y con una línea abajo. */
  enBandeja: boolean;
  boton: ReactNode;
  /** Para el lector de pantalla, cuando el botón es sólo un ícono. */
  etiqueta?: string;
  onBoton: () => void;
  children: ReactNode;
}) {
  return (
    <div
      role={tono === 'err' ? 'alert' : 'status'}
      style={{
        ...aviso(tono),
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '2px 4px 2px 14px',
        fontSize: 13,
        ...(enBandeja ? { borderRadius: 0, borderWidth: '0 0 1px' } : {}),
      }}
    >
      <span style={{ flex: 1, minWidth: 0 }}>{children}</span>
      <button
        type="button"
        onClick={onBoton}
        aria-label={etiqueta}
        style={{
          minHeight: 44,
          minWidth: 44,
          padding: '0 10px',
          border: 'none',
          background: 'none',
          font: 'inherit',
          fontWeight: 600,
          color: tono === 'ok' ? c.verde : c.texto2,
          cursor: 'pointer',
          display: 'grid',
          placeItems: 'center',
        }}
      >
        {boton}
      </button>
    </div>
  );
}

/**
 * Un rack de frente: una columna por posición y una fila por nivel, con el
 * nivel más alto arriba, como se lo ve parado en el pasillo.
 */
function RackGrilla({
  rack,
  cerrado,
  onAlternar,
  sobre,
  elegido,
  moviendo,
  viajero,
  resaltados,
  sugerido,
  soltable,
  agarrable,
  onHueco,
}: {
  rack: Rack;
  /** Plegado: sólo el título y cuánto tiene ocupado. */
  cerrado: boolean;
  onAlternar: () => void;
  sobre: string | null;
  elegido: string | null;
  moviendo: string | null;
  /** El pallet recién soltado, que viaja hasta su hueco. */
  viajero: string | null;
  /** Los pallets que coinciden con el buscador. */
  resaltados: Set<string>;
  /** El primer hueco libre para el elegido (`A1-04#1`). */
  sugerido: string | null;
  soltable: (clave: string, destino: Destino) => object;
  agarrable: (pallet: string) => object;
  onHueco: (destino: Destino) => void;
}) {
  const celular = useEsCelular();
  const huecos = rack.posiciones.flatMap((p) => p.niveles);
  if (rack.tipo === 'piso')
    return (
      <ZonaGrilla
        {...{ rack, cerrado, onAlternar, sobre, elegido, moviendo, viajero, resaltados, soltable, agarrable, onHueco }}
      />
    );
  const posiciones = [...rack.posiciones].sort((a, b) => a.columna - b.columna);
  const niveles = Array.from({ length: rack.niveles }, (_, i) => rack.niveles - i);
  // En el celular, tres huecos enteros a lo ancho: 96 px (en 390 entran
  // justo), o lo que dé el ancho sin bajar de 88 (en 360). A 96 fijos, en 360
  // el tercero quedaba cortado y había que correr el rack de costado para ver
  // si estaba libre. Con 88 el código del pallet entra gracias al relleno
  // más chico de `Fila`. Las columnas de más se siguen corriendo de costado.
  const columna = celular ? 'max(88px, min(96px, calc((100% - 40px) / 3)))' : '104px';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxWidth: '100%' }}>
      <button
        type="button"
        onClick={(e) => {
          // Sin cortar acá, el click llega a la página y suelta el pallet elegido.
          e.stopPropagation();
          onAlternar();
        }}
        aria-expanded={!cerrado}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          border: 'none',
          background: 'none',
          padding: 0,
          minHeight: 44,
          font: 'inherit',
          textAlign: 'left',
          cursor: 'pointer',
          fontSize: 12.5,
          fontWeight: 600,
          color: c.texto2,
        }}
      >
        <motion.span animate={{ rotate: cerrado ? -90 : 0 }} transition={suave} style={{ display: 'grid' }}>
          <Icono nombre="expand_more" size={18} color={c.texto3} />
        </motion.span>
        Rack {rack.numero} <span style={{ fontFamily: mono, fontWeight: 400, color: c.texto4 }}>{rack.id}</span>
        <AnimatePresence initial={false}>
          {cerrado && (
            <motion.span key="ocupados" {...pop} style={{ fontWeight: 400, color: c.texto3, display: 'inline-block' }}>
              · {huecos.filter((n) => n.ocupado).length} de {huecos.length} ocupados
            </motion.span>
          )}
        </AnimatePresence>
      </button>
      {/* Abrir el rack lo despliega desde su título hacia abajo; cerrarlo lo pliega ahí. */}
      <AnimatePresence initial={false}>
      {!cerrado && (
        <motion.div key="grilla" {...desplegar}>
        <div style={{ overflowX: 'auto', paddingBottom: 4 }}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: `28px repeat(${posiciones.length}, ${columna})`,
              gap: 4,
            }}
          >
            <span />
            {posiciones.map((p) => (
              <span key={p.codigo} style={{ fontSize: 12, fontFamily: mono, color: c.texto3, textAlign: 'center' }}>
                {p.codigo}
              </span>
            ))}
            {niveles.map((n) => (
              <Fila
                key={n}
                n={n}
                posiciones={posiciones}
                sobre={sobre}
                elegido={elegido}
                moviendo={moviendo}
                viajero={viajero}
                resaltados={resaltados}
                sugerido={sugerido}
                soltable={soltable}
                agarrable={agarrable}
                onHueco={onHueco}
              />
            ))}
          </div>
        </div>
        </motion.div>
      )}
      </AnimatePresence>
    </div>
  );
}

/**
 * Una zona de piso: sin niveles ni huecos fijos. Es un solo lugar donde se
 * suelta, con la lista de los pallets que ya tiene, que se agarran igual que
 * los de un rack.
 */
function ZonaGrilla({
  rack,
  cerrado,
  onAlternar,
  sobre,
  elegido,
  moviendo,
  viajero,
  resaltados,
  soltable,
  agarrable,
  onHueco,
}: Omit<Parameters<typeof RackGrilla>[0], 'sugerido'>) {
  const p = rack.posiciones[0];
  if (!p) return null;
  const pallets = p.zona ?? [];
  const clave = `${p.codigo}#zona`;
  const destino = { codigo: p.codigo, nivel: null };
  const encima = sobre === clave;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxWidth: '100%' }}>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onAlternar();
        }}
        aria-expanded={!cerrado}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          border: 'none',
          background: 'none',
          padding: 0,
          minHeight: 44,
          font: 'inherit',
          textAlign: 'left',
          cursor: 'pointer',
          fontSize: 12.5,
          fontWeight: 600,
          color: c.texto2,
        }}
      >
        <motion.span animate={{ rotate: cerrado ? -90 : 0 }} transition={suave} style={{ display: 'grid' }}>
          <Icono nombre="expand_more" size={18} color={c.texto3} />
        </motion.span>
        Zona de piso <span style={{ fontFamily: mono, fontWeight: 400, color: c.texto4 }}>{rack.id}</span>
        <span style={{ fontWeight: 400, color: c.texto3 }}>
          · {pallets.length} {pallets.length === 1 ? 'pallet' : 'pallets'}
        </span>
      </button>
      <AnimatePresence initial={false}>
      {!cerrado && (
        <motion.div key="zona" {...desplegar}>
        <div
          {...soltable(clave, destino)}
          onClick={(e) => {
            e.stopPropagation();
            onHueco(destino);
          }}
          aria-label={`Zona de piso ${rack.id}`}
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 4,
            minHeight: 58,
            width: 104 * 3 + 8,
            maxWidth: '100%',
            padding: 4,
            borderRadius: 10,
            border: `1.5px dashed ${encima ? c.verde : elegido ? c.verdeBorde : c.bordeTenue}`,
            background: encima ? c.verdeSuave : elegido ? c.verdeTinte : c.fondo,
            cursor: elegido ? 'pointer' : 'default',
            transition: 'background-color .34s cubic-bezier(.16,1,.3,1), border-color .34s cubic-bezier(.16,1,.3,1)',
          }}
        >
          {pallets.map((n) => (
            <Viaje key={n.pallet} pallet={n.pallet!} viajero={viajero}>
            <div
              {...agarrable(n.pallet!)}
              title={`${n.pallet} · ${n.articulo ?? ''} · ${n.tipo === 'completo' ? 'entero' : `${n.piezas ?? 0} pz`}`}
              style={{
                ...pastilla(elegido === n.pallet, moviendo === n.pallet),
                ...(resaltados.has(n.pallet!) && elegido !== n.pallet
                  ? { background: c.ambarSuave, border: `1px solid ${c.ambar}`, boxShadow: `0 0 0 2px ${c.ambar}` }
                  : {}),
                width: 100,
                height: 50,
                padding: '6px 8px',
              }}
            >
              <span style={{ fontFamily: mono, fontSize: 12, fontWeight: 600, color: c.texto, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{n.pallet}</span>
              <span style={{ fontSize: 12, color: c.texto3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{n.articulo}</span>
            </div>
            </Viaje>
          ))}
          {pallets.length === 0 && (
            <span style={{ alignSelf: 'center', margin: '0 auto', fontSize: 12, color: c.texto4 }}>Soltá acá: entran todos los que hagan falta</span>
          )}
        </div>
        </motion.div>
      )}
      </AnimatePresence>
    </div>
  );
}

function Fila({
  n,
  posiciones,
  sobre,
  elegido,
  moviendo,
  viajero,
  resaltados,
  sugerido,
  soltable,
  agarrable,
  onHueco,
}: {
  n: number;
  posiciones: Rack['posiciones'];
  sobre: string | null;
  elegido: string | null;
  moviendo: string | null;
  viajero: string | null;
  resaltados: Set<string>;
  sugerido: string | null;
  soltable: (clave: string, destino: Destino) => object;
  agarrable: (pallet: string) => object;
  onHueco: (destino: Destino) => void;
}) {
  // 6 px de costado en el celular: en un hueco de 88 el código del pallet entra entero.
  const relleno = useEsCelular() ? '6px 6px' : '6px 8px';
  return (
    <>
      <span style={{ fontSize: 12, color: c.texto4, alignSelf: 'center' }}>N{n}</span>
      {posiciones.map((p) => {
        const clave = `${p.codigo}#${n}`;
        const hueco: NivelPosicion | undefined = p.niveles.find((x) => x.nivel === n);
        const alto = { height: 50 };
        if (hueco?.pallet) {
          return (
            <Viaje key={clave} pallet={hueco.pallet} viajero={viajero}>
            <div
              {...agarrable(hueco.pallet)}
              title={`${hueco.pallet} · ${hueco.articulo ?? ''} · ${hueco.tipo === 'completo' ? 'entero' : `${hueco.piezas ?? 0} pz`}`}
              style={{
                ...pastilla(elegido === hueco.pallet, moviendo === hueco.pallet),
                // Coincide con el buscador: en ámbar, como las coincidencias del mapa.
                ...(resaltados.has(hueco.pallet) && elegido !== hueco.pallet
                  ? { background: c.ambarSuave, border: `1px solid ${c.ambar}`, boxShadow: `0 0 0 2px ${c.ambar}` }
                  : {}),
                ...alto,
                padding: relleno,
              }}
            >
              <span style={{ fontFamily: mono, fontSize: 12, fontWeight: 600, color: c.texto, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{hueco.pallet}</span>
              <span style={{ fontSize: 12, color: c.texto3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {hueco.articulo}
              </span>
            </div>
            </Viaje>
          );
        }
        const destino = { codigo: p.codigo, nivel: n };
        const encima = sobre === clave;
        const esSugerido = sugerido === clave;
        return (
          <div
            key={clave}
            {...soltable(clave, destino)}
            onClick={(e) => {
              e.stopPropagation();
              onHueco(destino);
            }}
            aria-label={`Hueco libre ${p.codigo} nivel ${n}${esSugerido ? ', el primero libre' : ''}`}
            style={{
              ...alto,
              borderRadius: 10,
              border: `1.5px ${esSugerido ? 'solid' : 'dashed'} ${encima || esSugerido ? c.verde : elegido ? c.verdeBorde : c.bordeTenue}`,
              background: encima ? c.verdeSuave : elegido ? c.verdeTinte : c.fondo,
              cursor: elegido ? 'pointer' : 'default',
              transition: 'background-color .34s cubic-bezier(.16,1,.3,1), border-color .34s cubic-bezier(.16,1,.3,1)',
              display: 'grid',
              placeItems: 'center',
              fontSize: 12,
              fontWeight: 600,
              color: c.verde,
            }}
          >
            <AnimatePresence>
              {esSugerido && (
                <motion.span key="sugerido" {...pop}>
                  Sugerido
                </motion.span>
              )}
            </AnimatePresence>
          </div>
        );
      })}
    </>
  );
}
